import { useEffect, useState } from "react";
import * as api from "../api.js";
import { Card } from "@/ui/card";
import { Spinner } from "@/ui/spinner";
import { useAcoes, useEstado } from "../estado.jsx";
import { Botao, Campo } from "./comuns.jsx";

/*
 * Sem atalho de entrada (29/10). Existia um "Entrar como … (teste)" com a
 * senha no pacote da página, ligado por `VITE_LOGIN_TESTE` — e a conta era a
 * do sócio, master, no endereço público. Saiu do código, não só do `.env`: um
 * botão que depende de alguém lembrar de não configurar volta na primeira build
 * com o `.env` errado.
 */

/**
 * A porta. Sem sessão, é a única coisa que a mesa mostra.
 *
 * O cartão é o `Card` do shadcn e as caixas são o `Campo` da casa — que já é o
 * trio `Field`/`FieldLabel`/`Input`, com o rótulo amarrado ao campo. O `<form>`
 * mora DENTRO do cartão: o cartão é a moldura, o formulário é o que ele contém,
 * e `Card` só sabe desenhar um `<div>`.
 */
/* O token chega pela URL do e-mail; sai dela assim que é lido, para o link não
   ficar no histórico nem ir junto num print da tela. */
function tokenDaUrl() {
  const token = new URLSearchParams(window.location.search).get("senha");
  if (token) window.history.replaceState(null, "", window.location.pathname);
  return token;
}

export default function Entrada() {
  const [token] = useState(tokenDaUrl);
  const [modo, setModo] = useState(token ? "nova" : "entrar");
  if (modo === "esqueci") return <Esqueci aoVoltar={() => setModo("entrar")} />;
  if (modo === "nova") return <SenhaNova token={token} aoPedirOutro={() => setModo("esqueci")} />;
  return <Entrar aoEsquecer={() => setModo("esqueci")} />;
}

/** A moldura das três telas da porta: marca, título e o que vai dentro. */
function Moldura({ titulo, frase, aoEnviar, children }) {
  return (
    <div className="flex h-dvh items-center justify-center bg-background p-5">
      <Card className="w-[min(380px,100%)] gap-0 rounded-[20px] border border-border p-[26px] shadow-[0_18px_44px_color-mix(in_oklab,_var(--color-foreground)_10%,_transparent)] ring-0 [--card-spacing:0px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            aoEnviar();
          }}
        >
          {/* Cartão claro: aqui vale a marca navy. */}
          <img src="/fga-mark.png" alt="Felipe Galera Advogados" className="mb-4 block h-[38px] w-auto" />
          <h1 className="m-0 mb-1 font-serif text-[27px] font-normal">{titulo}</h1>
          <p className="m-0 mb-5 text-corpo text-tinta-suave">{frase}</p>
          {children}
        </form>
      </Card>
    </div>
  );
}

const Recado = ({ erro, children }) =>
  children ? (
    <div role={erro ? "alert" : "status"} className={`mt-2.5 text-corpo leading-[1.45] ${erro ? "text-primary" : "text-verde"}`}>
      {children}
    </div>
  ) : null;

const Enviar = ({ rotulo, esperando, ocupado }) => (
  <Botao
    rotulo={
      ocupado ? (
        <>
          <Spinner className="size-3.5" />
          {esperando}
        </>
      ) : (
        rotulo
      )
    }
    tipo="submit"
    destaque
    desabilitado={ocupado}
    estilo="width:100%;margin-top:16px;font-size:14px;padding:13px;border-radius:12px"
  />
);

const Link = ({ rotulo, aoTocar }) => (
  <button
    type="button"
    onClick={aoTocar}
    className="alvo mx-auto mt-4 block cursor-pointer border-0 bg-transparent p-0 text-corpo text-tinta-suave underline-offset-4 hover:text-foreground hover:underline"
  >
    {rotulo}
  </button>
);

function Esqueci({ aoVoltar }) {
  const [email, setEmail] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState(null);
  async function enviar() {
    setOcupado(true);
    try {
      const r = await api.pedirLinkDeSenha(email);
      setRecado({ texto: r.recado });
    } catch (e) {
      setRecado({ erro: true, texto: e.message });
    } finally {
      setOcupado(false);
    }
  }
  return (
    <Moldura
      titulo="Esqueci a senha"
      frase="Escreva o e-mail com que você entra. Mandamos um link para criar a senha nova."
      aoEnviar={enviar}
    >
      <Campo rotulo="E-mail" dica="e-mail" tipo="email" autoPreencher="username" valor={email} aoMudar={setEmail} />
      <Recado erro={recado?.erro}>{recado?.texto}</Recado>
      <Enviar rotulo="Mandar o link" esperando="Mandando…" ocupado={ocupado} />
      <Link rotulo="Voltar para a entrada" aoTocar={aoVoltar} />
    </Moldura>
  );
}

function SenhaNova({ token, aoPedirOutro }) {
  const { entrar } = useAcoes();
  /* undefined = conferindo, null = link morto, string = o e-mail do login. */
  const [email, setEmail] = useState(undefined);
  const [nova, setNova] = useState("");
  const [repetida, setRepetida] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState("");
  useEffect(() => {
    api.conferirLinkDeSenha(token).then(
      (r) => setEmail(r.email),
      () => setEmail(null),
    );
  }, [token]);
  async function enviar() {
    if (nova !== repetida) return setErro("As duas senhas não são iguais.");
    setOcupado(true);
    setErro("");
    try {
      const r = await api.gravarSenhaPeloLink(token, nova);
      /* Quem acabou de escolher a senha não precisa digitá-la de novo. */
      await entrar(r.email, nova);
    } catch (e) {
      setErro(e.message);
    } finally {
      setOcupado(false);
    }
  }
  if (email === null)
    return (
      <Moldura titulo="Link vencido" frase="Este link já foi usado ou passou dos 30 minutos." aoEnviar={aoPedirOutro}>
        <Enviar rotulo="Pedir um link novo" />
      </Moldura>
    );
  return (
    <Moldura
      titulo="Senha nova"
      frase={email ? `Para ${email}. Use pelo menos 12 caracteres.` : "Conferindo o link…"}
      aoEnviar={enviar}
    >
      <Campo
        rotulo="Senha nova"
        dica="senha nova"
        tipo="password"
        autoPreencher="new-password"
        valor={nova}
        aoMudar={setNova}
        estilo="margin-bottom:9px"
      />
      <Campo
        rotulo="Repita a senha"
        dica="repita a senha"
        tipo="password"
        autoPreencher="new-password"
        valor={repetida}
        aoMudar={setRepetida}
      />
      <Recado erro>{erro}</Recado>
      <Enviar rotulo="Gravar e entrar" esperando="Gravando…" ocupado={ocupado || email === undefined} />
    </Moldura>
  );
}

function Entrar({ aoEsquecer }) {
  const { erroLogin } = useEstado();
  const { entrar } = useAcoes();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  /* Entrar é uma ida ao servidor com o banco talvez ainda acordando: sem sinal
     nenhum, o segundo clique manda a mesma senha de novo e a única resposta que
     a tela dá é o erro, se houver. `entrar` não relança — quem falha escreve em
     `erroLogin` —, então o `finally` é o que devolve o botão. */
  const [enviando, setEnviando] = useState(false);
  async function enviar(mail, pass) {
    setEnviando(true);
    try {
      await entrar(mail, pass);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Moldura
      titulo="Mesa de atendimento"
      frase="Felipe Galera Advogados — entre para continuar."
      aoEnviar={() => enviar(email, senha)}
    >
      <Campo
        rotulo="E-mail"
        dica="e-mail"
        tipo="email"
        autoPreencher="username"
        valor={email}
        aoMudar={setEmail}
        estilo="margin-bottom:9px"
      />
      <Campo rotulo="Senha" dica="senha" tipo="password" autoPreencher="current-password" valor={senha} aoMudar={setSenha} />
      {/* `role="alert"`: a recusa da entrada é a única resposta desta tela, e sem
          isso ela acontecia em silêncio para quem usa leitor de tela. */}
      <Recado erro>{erroLogin}</Recado>
      <Enviar rotulo="Entrar" esperando="Entrando…" ocupado={enviando} />
      <Link rotulo="Esqueci a senha" aoTocar={aoEsquecer} />
    </Moldura>
  );
}
